import React, { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw, Ban, XCircle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Card, Btn } from "../UI";

const STATUS = { open: "text-amber-300 bg-amber-400/10 border-amber-400/30", dismissed: "text-slate-400 bg-white/5 border-white/10", actioned: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30" };
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const FlagRow = ({ f, onAct }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex flex-wrap items-center gap-3" data-testid={`abuse-flag-${f.id}`}>
    <span className={`pill ${STATUS[f.status]}`} data-testid="abuse-flag-status">{f.status}</span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-white">{f.label} <span className="text-slate-500 font-normal">· {f.count} / {f.threshold} {f.description}</span></div>
      <div className="text-[11px] text-slate-400 mt-0.5 truncate">
        {f.subject_type === "ip" ? `network ${f.subject}` : `card ${f.evidence?.code || f.subject}`}
        {f.evidence?.codes ? ` · tried ${f.evidence.codes.slice(0, 4).join(", ")}${f.evidence.codes.length > 4 ? "…" : ""}` : ""}
        {f.evidence?.distinct_ips != null ? ` · ${f.evidence.distinct_ips} networks` : ""}
        {f.evidence?.accounts != null ? ` · ${f.evidence.accounts} accounts` : ""}
        {` · ${when(f.first_seen)} → ${when(f.last_seen)}`}
      </div>
    </div>
    {f.status === "open" && (
      <div className="flex items-center gap-2">
        {f.subject_type === "ip"
          ? <Btn variant="danger" className="!px-3 !py-1.5 !text-xs" onClick={() => onAct(f, "block-ip")} data-testid="abuse-block-ip"><Ban className="w-3.5 h-3.5" /> Block network</Btn>
          : <Btn variant="danger" className="!px-3 !py-1.5 !text-xs" onClick={() => onAct(f, "void-card")} data-testid="abuse-void-card"><XCircle className="w-3.5 h-3.5" /> Void card</Btn>}
        <Btn variant="secondary" className="!px-3 !py-1.5 !text-xs" onClick={() => onAct(f, "dismiss")} data-testid="abuse-dismiss"><CheckCircle2 className="w-3.5 h-3.5" /> Dismiss</Btn>
      </div>
    )}
  </div>
);

// Admin queue of suspicious scan / claim patterns (rules + throttles live in backend/routes/promo_abuse.py).
export const AbuseQueue = () => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const load = () => api.get("/promotions/abuse/queue").then(({ data }) => setQ(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/promotions/abuse/refresh"); setQ((p) => ({ ...(p || {}), ...data })); toast.success(`Scan complete — ${data.open} open flag${data.open === 1 ? "" : "s"}`); }
    catch (e) { toast.error(errMsg(e, "Could not refresh")); } finally { setBusy(false); }
  };
  const act = async (f, action) => {
    try { await api.post(`/promotions/abuse/flags/${f.id}/${action}`); toast.success({ dismiss: "Flag dismissed", "block-ip": "Network blocked", "void-card": "Card voided" }[action]); load(); }
    catch (e) { toast.error(errMsg(e, "Action failed")); }
  };
  if (!q?.flags) return null;
  const flags = showAll ? q.flags : q.flags.filter((f) => f.status === "open");
  return (
    <Card className="!p-5" data-testid="abuse-queue">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><ShieldAlert className="w-4 h-4 text-amber-300" /> Abuse review queue
            <span className="pill pill-paused" data-testid="abuse-open-count">{q.open} open</span>
            {q.blocked_ips > 0 && <span className="pill pill-rejected" data-testid="abuse-blocked-count">{q.blocked_ips} blocked</span>}
          </div>
          <p className="text-xs text-slate-400 mt-1">Claim links are throttled to {q.throttle?.preview_per_10m} opens / 10 min and {q.throttle?.invalid_codes_per_15m} bad codes / 15 min per network. Flags below need a human call.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAll((v) => !v)} className="text-xs text-slate-400 hover:text-white" data-testid="abuse-toggle-all">{showAll ? "Open only" : "Show resolved"}</button>
          <Btn variant="secondary" onClick={refresh} disabled={busy} data-testid="abuse-refresh">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Scan now</Btn>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {flags.length === 0 && <div className="text-sm text-slate-500 py-4 text-center" data-testid="abuse-empty">No {showAll ? "" : "open "}flags — scans and claims look clean.</div>}
        {flags.map((f) => <FlagRow key={f.id} f={f} onAct={act} />)}
      </div>
    </Card>
  );
};
